import React, { useState, useEffect } from 'react';
import { getProjectNeeds } from '../../project-comparison/utils/projectNeedsMapping';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const QuotesheetGenerator = ({ formData, onGenerate, onGenerateLite, autoGenerateLite, onAutoLiteComplete }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = () => {
    setIsGenerating(true);
    
    // Get category-specific needs from projectNeedsMapping
    const purchaseType = formData?.basics?.purchaseType;
    const categoryNeeds = getProjectNeeds(purchaseType);
    
    // Ensure categoryNeeds is an array, fallback to empty array if undefined
    const validCategoryNeeds = categoryNeeds || [];
    
    // Build question-based quotesheet structure starting with project needs
    const quotesheet = {
      id: `quotesheet_${Date.now()}`,
      projectCategory: purchaseType,
      projectName: formData?.basics?.projectName,
      generatedAt: new Date()?.toISOString(),
      sections: [
        {
          id: 'vendor-info',
          title: 'Vendor Information',
          fields: [
            { id: 'businessName', label: 'What is your business name?', type: 'text', required: true },
            { id: 'contactName', label: 'Who is the primary contact person?', type: 'text', required: true },
            { id: 'email', label: 'What is your business email address?', type: 'email', required: true },
            { id: 'phone', label: 'What is your contact phone number?', type: 'tel', required: true },
            { id: 'website', label: 'What is your business website? (Optional)', type: 'url', required: false }
          ]
        },
        {
          id: 'project-needs',
          title: `Project Requirements - ${purchaseType?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}`,
          description: 'Please answer the following questions based on the specific needs outlined for this project.',
          fields: validCategoryNeeds?.map(need => {
            // Convert needs into questions
            let questionLabel = '';
            let fieldType = 'text';
            let prefix = undefined;
            
            switch(need?.id) {
              case 'price':
                questionLabel = 'What is your total quoted price for this project?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'processor':
                questionLabel = 'What processor/chip specifications can you provide?';
                break;
              case 'memory':
                questionLabel = 'What memory (RAM) capacity does your offering include?';
                break;
              case 'storage':
                questionLabel = 'What storage capacity can you provide?';
                break;
              case 'delivery': case'timeline':
                questionLabel = 'What is your estimated delivery/completion timeline?';
                break;
              case 'warranty':
                questionLabel = 'What warranty or guarantee do you offer?';
                fieldType = 'textarea';
                break;
              case 'reviews':
                questionLabel = 'Can you provide customer references or review links?';
                fieldType = 'textarea';
                break;
              case 'screenSize':
                questionLabel = 'What screen size options can you provide?';
                break;
              case 'batteryLife':
                questionLabel = 'What battery life can you guarantee?';
                break;
              case 'camera':
                questionLabel = 'What camera specifications does your offering include?';
                break;
              case 'resolution':
                questionLabel = 'What resolution quality can you provide?';
                break;
              case 'smartFeatures':
                questionLabel = 'What smart features are included?';
                fieldType = 'textarea';
                break;
              case 'soundQuality':
                questionLabel = 'What sound quality specifications can you provide?';
                break;
              case 'compatibility':
                questionLabel = 'What compatibility does your product/service offer?';
                fieldType = 'textarea';
                break;
              case 'features':
                questionLabel = 'What key features does your offering include?';
                fieldType = 'textarea';
                break;
              case 'easeOfUse':
                questionLabel = 'How would you describe the ease of use?';
                fieldType = 'textarea';
                break;
              case 'security':
                questionLabel = 'What security features do you provide?';
                fieldType = 'textarea';
                break;
              case 'connectivity':
                questionLabel = 'What connectivity options are available?';
                break;
              case 'comfort':
                questionLabel = 'What comfort features does your product offer?';
                break;
              case 'healthFeatures':
                questionLabel = 'What health and fitness tracking features are included?';
                fieldType = 'textarea';
                break;
              case 'design':
                questionLabel = 'What design options and styles can you provide?';
                break;
              case 'purchasePrice':
                questionLabel = 'What is the total purchase price?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'interestRate':
                questionLabel = 'What interest rate can you offer?';
                fieldType = 'number';
                prefix = undefined;
                break;
              case 'downPayment':
                questionLabel = 'What down payment amount is required?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'loanTerm':
                questionLabel = 'What loan term options do you offer?';
                break;
              case 'closingCosts':
                questionLabel = 'What are the estimated closing costs?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'lenderReputation':
                questionLabel = 'Can you provide information about your company reputation and ratings?';
                fieldType = 'textarea';
                break;
              case 'monthlySavings':
                questionLabel = 'What monthly savings can you provide?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'breakEvenPoint':
                questionLabel = 'What is the estimated break-even point?';
                break;
              case 'fuelEfficiency':
                questionLabel = 'What fuel efficiency rating does this vehicle have?';
                break;
              case 'totalCost':
                questionLabel = 'What is the total price including all fees and taxes?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'makeModelYear':
                questionLabel = 'What is the make, model, and year of the vehicle?';
                break;
              case 'mileage':
                questionLabel = 'What is the current mileage on the vehicle (if used)?';
                break;
              case 'conditionReport':
                questionLabel = 'Can you provide a full condition report or inspection details?';
                fieldType = 'textarea';
                break;
              case 'vinHistory':
                questionLabel = 'Is a VIN history report (Carfax/AutoCheck) available? Please summarize key findings.';
                fieldType = 'textarea';
                break;
              case 'financingTerms':
                questionLabel = 'What financing terms are available (APR, loan term, monthly payment)?';
                fieldType = 'textarea';
                break;
              case 'insuranceRequirements':
                questionLabel = 'What insurance coverage is required and what is the estimated monthly premium?';
                fieldType = 'textarea';
                break;
              case 'safety':
                questionLabel = 'What safety features and ratings does this vehicle have?';
                fieldType = 'textarea';
                break;
              case 'reliability':
                questionLabel = 'What reliability ratings or guarantees can you provide?';
                break;
              case 'materialQuality':
                questionLabel = 'What material quality and grade do you use?';
                fieldType = 'textarea';
                break;
              case 'experience':
                questionLabel = 'How many years of experience does your company have?';
                break;
              case 'efficiency':
                questionLabel = 'What energy efficiency rating can you provide?';
                break;
              case 'brandReputation':
                questionLabel = 'What brands do you work with and what is their reputation?';
                fieldType = 'textarea';
                break;
              case 'maintenance':
                questionLabel = 'What maintenance plans or services do you offer?';
                fieldType = 'textarea';
                break;
              case 'availability':
                questionLabel = 'What is your availability and response time?';
                break;
              case 'licensing':
                questionLabel = 'What licenses and certifications do you hold?';
                fieldType = 'textarea';
                break;
              case 'paintQuality':
                questionLabel = 'What paint quality and brands do you use?';
                break;
              case 'durability':
                questionLabel = 'What is the expected durability and lifespan?';
                break;
              case 'mattressSize':
                questionLabel = 'What size options are available (Twin, Full, Queen, King, Cal King)?';
                fieldType = 'select';
                break;
              case 'firmness':
                questionLabel = 'What firmness levels do you offer (Soft, Medium-Soft, Medium, Medium-Firm, Firm)?';
                break;
              case 'materialType':
                questionLabel = 'What material type(s) does this mattress use (memory foam, hybrid, innerspring, latex, etc.)?';
                fieldType = 'textarea';
                break;
              case 'trialPeriod':
                questionLabel = 'What sleep trial period do you offer and what are the return terms?';
                fieldType = 'textarea';
                break;
              case 'deliveryHaulAway':
                questionLabel = 'What are your delivery fees and do you offer haul-away of the old mattress?';
                fieldType = 'textarea';
                break;
              case 'motoPrice':
                questionLabel = 'What is the total purchase price for this motorcycle?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'motoMakeModelYear':
                questionLabel = 'What is the make, model, and year of the motorcycle?';
                break;
              case 'motoMileage':
                questionLabel = 'What is the current mileage on the motorcycle (if used)?';
                break;
              case 'motoEngineSize':
                questionLabel = 'What is the engine displacement (CC) of the motorcycle?';
                break;
              case 'motoCondition':
                questionLabel = 'What is the overall condition and can you provide service/maintenance history?';
                fieldType = 'textarea';
                break;
              case 'motoTitleStatus':
                questionLabel = 'What is the title status of the motorcycle (clean, salvage, rebuilt, etc.)?';
                break;
              case 'motoInsurance':
                questionLabel = 'What is the estimated insurance cost for this motorcycle?';
                break;
              case 'motoWarranty':
                questionLabel = 'What warranty coverage is included (manufacturer or dealer)?';
                fieldType = 'textarea';
                break;
              case 'motoSafetyFeatures':
                questionLabel = 'What safety features does this motorcycle have (ABS, traction control, etc.)?';
                fieldType = 'textarea';
                break;
              case 'motoRegistration':
                questionLabel = 'What are the estimated registration fees and applicable taxes?';
                break;
              case 'boatPrice':
                questionLabel = 'What is the total purchase price for this boat?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'boatType':
                questionLabel = 'What type of boat is this (fishing, pontoon, sailboat, etc.)?';
                break;
              case 'boatLength':
                questionLabel = 'What is the overall length and beam (width) of the boat?';
                break;
              case 'boatEngine':
                questionLabel = 'What is the engine type, horsepower, and current hours of use?';
                fieldType = 'textarea';
                break;
              case 'hullCondition':
                questionLabel = 'What is the current condition of the hull (structural and cosmetic)?';
                fieldType = 'textarea';
                break;
              case 'maintenanceHistory':
                questionLabel = 'Can you provide the maintenance history and service records?';
                fieldType = 'textarea';
                break;
              case 'storageRequirements':
                questionLabel = 'What are the storage requirements (marina slip, dry storage, trailer, etc.)?';
                break;
              case 'insuranceCost':
                questionLabel = 'What is the estimated annual insurance cost for this boat?';
                break;
              case 'registrationLicensing':
                questionLabel = 'What are the registration fees and licensing requirements?';
                fieldType = 'textarea';
                break;
              case 'trailerIncluded':
                questionLabel = 'Is a trailer included with the boat? If yes, please provide details.';
                break;
              case 'rvPrice':
                questionLabel = 'What is the total purchase price for this RV?';
                fieldType = 'number';
                prefix = '$';
                break;
              case 'rvType':
                questionLabel = 'What type of RV is this (Class A, B, C, Travel Trailer, Fifth Wheel, etc.)?';
                break;
              case 'rvMileage':
                questionLabel = 'What is the current mileage on this RV (if motorized)?';
                break;
              case 'rvFloorPlan':
                questionLabel = 'What is the floor plan layout and sleeping capacity of this RV?';
                break;
              case 'rvEngineType':
                questionLabel = 'What is the engine type and specifications (if motorized)?';
                break;
              case 'rvTowingCapacity':
                questionLabel = 'What is the towing capacity or required tow vehicle rating (if towable)?';
                break;
              case 'rvCondition':
                questionLabel = 'What is the overall condition and can you provide maintenance/service history?';
                fieldType = 'textarea';
                break;
              case 'rvInsurance':
                questionLabel = 'What is the estimated annual insurance cost for this RV?';
                break;
              case 'rvStorage':
                questionLabel = 'What are the storage requirements for this RV?';
                break;
              case 'rvWarranty':
                questionLabel = 'What warranty coverage is included (manufacturer or dealer)?';
                fieldType = 'textarea';
                break;
              case 'carInsurancePremium':
                questionLabel = 'What is the monthly and annual premium for this policy?';
                fieldType = 'text';
                break;
              case 'coverageLimits':
                questionLabel = 'What are the coverage limits for each coverage type?';
                fieldType = 'textarea';
                break;
              case 'deductibles':
                questionLabel = 'What deductible amounts are available for comprehensive and collision coverage?';
                break;
              case 'liabilityCoverage':
                questionLabel = 'What liability coverage limits do you offer (bodily injury and property damage)?';
                fieldType = 'textarea';
                break;
              case 'comprehensiveCollision':
                questionLabel = 'What comprehensive and collision coverage options are available?';
                fieldType = 'textarea';
                break;
              case 'uninsuredMotorist':
                questionLabel = 'What uninsured/underinsured motorist coverage do you provide?';
                fieldType = 'textarea';
                break;
              case 'discountsAvailable':
                questionLabel = 'What discounts are available (multi-policy, safe driver, good student, etc.)?';
                fieldType = 'textarea';
                break;
              case 'policyTermLength':
                questionLabel = 'What policy term lengths do you offer (6-month, 12-month)?';
                break;
              case 'exclusions':
                questionLabel = 'What exclusions or limitations apply to this policy?';
                fieldType = 'textarea';
                break;
              case 'claimsServiceRating':
                questionLabel = 'How do you handle claims and what is your customer service rating (J.D. Power, BBB, etc.)?';
                fieldType = 'textarea';
                break;
              case 'weddingVenueCost':
                questionLabel = 'What is your total venue cost or package pricing?';
                fieldType = 'text';
                break;
              case 'weddingGuestCapacity':
                questionLabel = 'What is the maximum guest capacity you can accommodate?';
                fieldType = 'text';
                break;
              case 'weddingCateringCost':
                questionLabel = 'What is your catering cost per person (including menu options)?';
                fieldType = 'textarea';
                break;
              case 'weddingDecorFloral':
                questionLabel = 'What decor and floral packages do you offer and at what price?';
                fieldType = 'textarea';
                break;
              case 'weddingPhotography':
                questionLabel = 'What are your photographer/videographer fees and what packages are included?';
                fieldType = 'textarea';
                break;
              case 'weddingEntertainment':
                questionLabel = 'What entertainment or music options do you offer and what are the costs?';
                fieldType = 'textarea';
                break;
              case 'weddingContractTerms':
                questionLabel = 'What are the key contract terms and conditions for your services?';
                fieldType = 'textarea';
                break;
              case 'weddingDepositRefund':
                questionLabel = 'What is your deposit requirement and refund/cancellation policy?';
                fieldType = 'textarea';
                break;
              case 'eventVenueCost':
                questionLabel = 'What is your total venue cost or package pricing?';
                fieldType = 'text';
                break;
              case 'eventGuestCapacity':
                questionLabel = 'What is the maximum guest capacity you can accommodate?';
                fieldType = 'text';
                break;
              case 'eventCateringCost':
                questionLabel = 'What is your catering cost per person (including menu options)?';
                fieldType = 'textarea';
                break;
              case 'eventDecorFloral':
                questionLabel = 'What decor and floral packages do you offer and at what price?';
                fieldType = 'textarea';
                break;
              case 'eventPhotography':
                questionLabel = 'What are your photographer/videographer fees and what packages are included?';
                fieldType = 'textarea';
                break;
              case 'eventEntertainment':
                questionLabel = 'What entertainment or music options do you offer and what are the costs?';
                fieldType = 'textarea';
                break;
              case 'eventContractTerms':
                questionLabel = 'What are the key contract terms and conditions for your services?';
                fieldType = 'textarea';
                break;
              case 'eventDepositRefund':
                questionLabel = 'What is your deposit requirement and refund/cancellation policy?';
                fieldType = 'textarea';
                break;
              case 'ringTotalPrice':
                questionLabel = 'What is your total price for this engagement ring?';
                fieldType = 'text';
                prefix = '$';
                break;
              case 'ringStoneType':
                questionLabel = 'What stone type(s) do you offer (diamond, moissanite, sapphire, etc.)?';
                fieldType = 'text';
                break;
              case 'ringCaratRange':
                questionLabel = 'What carat weight range can you provide?';
                fieldType = 'text';
                break;
              case 'ringClarity':
                questionLabel = 'What clarity grades do you carry or recommend?';
                fieldType = 'text';
                break;
              case 'ringColor':
                questionLabel = 'What color grades do you carry or recommend?';
                fieldType = 'text';
                break;
              case 'ringCut':
                questionLabel = 'What cut quality do you offer or recommend?';
                fieldType = 'text';
                break;
              case 'ringMetalType':
                questionLabel = 'What metal types do you offer (platinum, white/yellow/rose gold, etc.)?';
                fieldType = 'text';
                break;
              case 'ringCertification':
                questionLabel = 'What certifications do your stones carry (GIA, AGS, IGI, etc.)?';
                fieldType = 'text';
                break;
              case 'ringWarranty':
                questionLabel = 'What warranty do you offer on the ring and stone?';
                fieldType = 'textarea';
                break;
              case 'ringInsurance':
                questionLabel = 'What insurance options do you offer or recommend for the ring?';
                fieldType = 'textarea';
                break;
              case 'childcareCost':
                questionLabel = 'What is your weekly or monthly cost for childcare/daycare?';
                fieldType = 'text';
                break;
              case 'childcareHours':
                questionLabel = 'What hours of care are included (daily/weekly)?';
                fieldType = 'text';
                break;
              case 'childcareAgeRange':
                questionLabel = 'What age range of children do you accept?';
                fieldType = 'text';
                break;
              case 'childcareStaffRatio':
                questionLabel = 'What is your staff-to-child ratio by age group?';
                fieldType = 'text';
                break;
              case 'childcareMeals':
                questionLabel = 'Are meals and snacks included in your program?';
                fieldType = 'text';
                break;
              case 'childcareCurriculum':
                questionLabel = 'What curriculum or educational approach do you use?';
                fieldType = 'textarea';
                break;
              case 'childcareLicensing':
                questionLabel = 'What is your licensing status and safety certification history?';
                fieldType = 'textarea';
                break;
              case 'schoolAnnualCost':
                questionLabel = 'What is your annual tuition cost?';
                fieldType = 'text';
                prefix = '$';
                break;
              case 'schoolFees':
                questionLabel = 'What additional fees are charged (books, uniforms, technology, etc.)?';
                fieldType = 'textarea';
                break;
              case 'schoolPaymentSchedule':
                questionLabel = 'What payment schedule options do you offer (monthly, semester, annual)?';
                fieldType = 'text';
                break;
              case 'schoolTransportation':
                questionLabel = 'What transportation options are available and at what cost?';
                fieldType = 'textarea';
                break;
              case 'schoolAdmissionRequirements':
                questionLabel = 'What are your admission requirements and application process?';
                fieldType = 'textarea';
                break;
              case 'strollerPrice':
                questionLabel = 'What is the price or price range for this stroller?';
                fieldType = 'text';
                prefix = '$';
                break;
              case 'strollerWeightFoldSize':
                questionLabel = 'What is the weight and folded size of this stroller?';
                fieldType = 'text';
                break;
              case 'strollerCarSeatCompatibility':
                questionLabel = 'Which infant car seat brands or models is this stroller compatible with?';
                fieldType = 'text';
                break;
              case 'strollerWeightCapacity':
                questionLabel = 'What is the maximum weight capacity for this stroller?';
                fieldType = 'text';
                break;
              case 'strollerWheelType':
                questionLabel = 'What type of wheels does this stroller have (jogging, travel, all-terrain, etc.)?';
                fieldType = 'text';
                break;
              case 'strollerSuspension':
                questionLabel = 'What type of suspension system does this stroller have?';
                fieldType = 'text';
                break;
              case 'strollerStorageBasket':
                questionLabel = 'How large is the storage basket on this stroller?';
                fieldType = 'text';
                break;
              case 'strollerOneHandFold':
                questionLabel = 'Does this stroller have one-hand fold capability?';
                fieldType = 'text';
                break;
              case 'strollerTravelSystem':
                questionLabel = 'Is a travel system included with this stroller?';
                fieldType = 'text';
                break;
              case 'strollerWarranty':
                questionLabel = 'What warranty is included with this stroller?';
                fieldType = 'text';
                break;
              case 'cribPrice':
                questionLabel = 'What is the price or price range for this crib?';
                fieldType = 'text';
                prefix = '$';
                break;
              case 'cribDimensions':
                questionLabel = 'What are the overall dimensions of this crib (L x W x H)?';
                fieldType = 'text';
                break;
              case 'cribMattressSize':
                questionLabel = 'What mattress size is compatible with this crib?';
                fieldType = 'text';
                break;
              case 'cribAdjustableHeight':
                questionLabel = 'How many adjustable mattress height levels does this crib have?';
                fieldType = 'text';
                break;
              case 'cribConvertsToToddler':
                questionLabel = 'Does this crib convert to a toddler bed?';
                fieldType = 'text';
                break;
              case 'cribMaterial':
                questionLabel = 'What materials are used in this crib (solid wood, MDF, etc.)?';
                fieldType = 'text';
                break;
              case 'cribSafetyCertifications':
                questionLabel = 'What safety certifications does this crib have (JPMA, ASTM)?';
                fieldType = 'text';
                break;
              case 'cribWeightCapacity':
                questionLabel = 'What is the weight capacity of this crib?';
                fieldType = 'text';
                break;
              case 'cribAssembly':
                questionLabel = 'What are the assembly requirements for this crib?';
                fieldType = 'text';
                break;
              case 'cribWarranty':
                questionLabel = 'What warranty is included with this crib?';
                fieldType = 'text';
                break;
              case 'cribBrand':
                questionLabel = 'What is the brand and model of the crib?';
                fieldType = 'text';
                break;
              case 'cribType':
                questionLabel = 'What type of crib is this (standard, convertible, mini, portable, etc.)?';
                fieldType = 'text';
                break;
              case 'cribSafetyRating':
                questionLabel = 'What safety certifications does this crib have (JPMA, ASTM, CPSC)?';
                fieldType = 'text';
                break;
              case 'cribMattressIncluded':
                questionLabel = 'Is a mattress included with this crib?';
                fieldType = 'text';
                break;
              case 'carSeatPrice':
                questionLabel = 'What is the price or price range for this car seat?';
                fieldType = 'text';
                prefix = '$';
                break;
              case 'carSeatBrand':
                questionLabel = 'What is the brand and model of the car seat?';
                fieldType = 'text';
                break;
              case 'carSeatType':
                questionLabel = 'What type of car seat is this (infant, convertible, booster, all-in-one)?';
                fieldType = 'text';
                break;
              case 'carSeatWeightRange':
                questionLabel = 'What is the supported weight and height range for this car seat?';
                fieldType = 'text';
                break;
              case 'carSeatSafetyRating':
                questionLabel = 'What safety ratings and certifications does this car seat have?';
                fieldType = 'text';
                break;
              case 'carSeatInstallType':
                questionLabel = 'What installation method does this car seat use (LATCH, seatbelt, base)?';
                fieldType = 'text';
                break;
              case 'carSeatWarranty':
                questionLabel = 'What warranty is included with this car seat?';
                fieldType = 'text';
                break;
              case 'bassinetPrice':
                questionLabel = 'What is the price or price range for this bassinet?';
                fieldType = 'text';
                prefix = '$';
                break;
              case 'bassinetWeightLimit':
                questionLabel = 'What is the maximum infant weight capacity for this bassinet?';
                fieldType = 'text';
                break;
              case 'bassinetSize':
                questionLabel = 'What are the dimensions and how portable is this bassinet?';
                fieldType = 'text';
                break;
              case 'bassinetBreathableMesh':
                questionLabel = 'Does this bassinet have breathable mesh sides?';
                fieldType = 'text';
                break;
              case 'bassinetAdjustableHeight':
                questionLabel = 'Is the height adjustable, and what are the height settings?';
                fieldType = 'text';
                break;
              case 'bassinetBedsideSleeper':
                questionLabel = 'Does this bassinet support bedside sleeper / co-sleeper use?';
                fieldType = 'text';
                break;
              case 'bassinetRocking':
                questionLabel = 'Is this bassinet rocking or stationary? Does it have vibration?';
                fieldType = 'text';
                break;
              case 'bassinetSafetyCerts':
                questionLabel = 'What safety certifications does this bassinet have (ASTM, CPSC, JPMA)?';
                fieldType = 'text';
                break;
              case 'bassinetMattressIncluded':
                questionLabel = 'Is a mattress included with this bassinet?';
                fieldType = 'text';
                break;
              case 'bassinetWarranty':
                questionLabel = 'What warranty is included with this bassinet?';
                fieldType = 'text';
                break;
              default:
                questionLabel = `What can you tell us about ${need?.name?.toLowerCase()}?`;
                break;
            }
            
            return {
              id: need?.id,
              label: questionLabel,
              type: fieldType,
              required: need?.priority === 'High',
              description: need?.description,
              prefix: prefix,
              needName: need?.name,
              priority: need?.priority
            };
          })
        },
        {
          id: 'pricing-breakdown',
          title: 'Pricing Details',
          fields: [
            { id: 'laborCost', label: 'What is the labor cost breakdown?', type: 'number', required: false, prefix: '$' },
            { id: 'materialCost', label: 'What is the material cost breakdown?', type: 'number', required: false, prefix: '$' },
            { id: 'additionalFees', label: 'Are there any additional fees? If yes, please specify.', type: 'number', required: false, prefix: '$' },
            { id: 'pricingBreakdown', label: 'Can you provide a detailed pricing breakdown?', type: 'textarea', required: formData?.quoteSettings?.requirements?.includes('pricing-breakdown') }
          ]
        },
        {
          id: 'timeline-details',
          title: 'Timeline & Availability',
          fields: [
            { id: 'startDate', label: 'What is your earliest available start date?', type: 'date', required: false },
            { id: 'completionDate', label: 'What is your expected completion date?', type: 'date', required: false },
            { id: 'milestones', label: 'What are the key milestones in your proposed timeline?', type: 'textarea', required: false }
          ]
        },
        {
          id: 'payment-terms',
          title: 'Payment Terms',
          fields: [
            { id: 'paymentTerms', label: 'What payment terms do you offer?', type: 'select', required: formData?.quoteSettings?.requirements?.includes('payment-terms'), options: [
              { value: 'net30', label: 'Net 30' },
              { value: 'net60', label: 'Net 60' },
              { value: '50-50', label: '50% Upfront, 50% on Completion' },
              { value: 'full-upfront', label: 'Full Payment Upfront' },
              { value: 'custom', label: 'Custom Terms' }
            ]},
            { id: 'paymentMethods', label: 'What payment methods do you accept?', type: 'text', required: false },
            { id: 'depositRequired', label: 'Is a deposit required? If yes, how much?', type: 'text', required: false }
          ]
        },
        {
          id: 'credentials',
          title: 'Credentials & Experience',
          fields: [
            { id: 'licenseNumber', label: 'What is your license number?', type: 'text', required: formData?.quoteSettings?.requirements?.includes('insurance') },
            { id: 'insuranceInfo', label: 'What insurance coverage do you carry?', type: 'textarea', required: formData?.quoteSettings?.requirements?.includes('insurance') },
            { id: 'references', label: 'Can you provide references or portfolio examples?', type: 'textarea', required: formData?.quoteSettings?.requirements?.includes('references') },
            { id: 'certifications', label: 'What certifications does your company hold?', type: 'text', required: false },
            { id: 'yearsInBusiness', label: 'How many years has your company been in business?', type: 'number', required: false }
          ]
        },
        {
          id: 'proposal-details',
          title: 'Detailed Proposal',
          fields: [
            { id: 'approach', label: 'What is your approach and methodology for this project?', type: 'textarea', required: formData?.quoteSettings?.requirements?.includes('specifications') },
            { id: 'differentiators', label: 'What sets your proposal apart from competitors?', type: 'textarea', required: false },
            { id: 'specialTerms', label: 'Are there any special terms or conditions we should be aware of?', type: 'textarea', required: false },
            { id: 'attachments', label: 'Please upload any supporting documents (portfolio, certifications, etc.)', type: 'file', required: false, accept: '.pdf,.doc,.docx,.jpg,.png' }
          ]
        }
      ]
    };

    // Add custom instructions if provided
    if (formData?.quoteSettings?.instructions) {
      quotesheet.customInstructions = formData?.quoteSettings?.instructions;
    }

    setTimeout(() => {
      setIsGenerating(false);
      onGenerate(quotesheet);
    }, 1000);
  };

  const handleGenerateLite = () => {
    setIsGenerating(true);

    const purchaseType = formData?.basics?.purchaseType;

    const quotesheetLite = {
      id: `quotesheet_lite_${Date.now()}`,
      projectCategory: purchaseType,
      projectName: formData?.basics?.projectName,
      generatedAt: new Date()?.toISOString(),
      type: 'lite',
      sections: [
        {
          id: 'vendor-info',
          title: 'Vendor Information',
          fields: [
            { id: 'businessName', label: 'What is your business name?', type: 'text', required: true },
            { id: 'contactName', label: 'Who is the primary contact person?', type: 'text', required: true },
            { id: 'email', label: 'What is your business email address?', type: 'email', required: true },
            { id: 'phone', label: 'What is your contact phone number?', type: 'tel', required: true }
          ]
        },
        {
          id: 'quote-essentials',
          title: 'Quote Essentials',
          fields: [
            { id: 'totalPrice', label: 'What is your total quoted price for this project?', type: 'number', required: true, prefix: '$' },
            { id: 'timeline', label: 'What is your estimated completion timeline?', type: 'text', required: true },
            { id: 'scopeSummary', label: 'Briefly describe what is included in your quote.', type: 'textarea', required: true }
          ]
        },
        {
          id: 'contact-preferences',
          title: 'Vendor Permitted Contact & Preferences',
          fields: [
            {
              id: 'allowPhoneCalls',
              label: 'Are you available for a phone consultation before submitting your quote?',
              type: 'select',
              required: false,
              options: [
                { value: 'yes', label: 'Yes, I am available for a call' },
                { value: 'no', label: 'No, please contact via email only' },
                { value: 'if-needed', label: 'Only if needed' }
              ]
            },
            {
              id: 'allowSiteVisit',
              label: 'Are you able to conduct a site visit or in-person assessment if applicable?',
              type: 'select',
              required: false,
              options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'na', label: 'Not applicable for this project' }
              ]
            },
            { id: 'preferredContactMethod', label: 'What is your preferred contact method?', type: 'text', required: false },
            { id: 'bestTimeToContact', label: 'What is the best time to reach you?', type: 'text', required: false }
          ]
        }
      ]
    };

    if (formData?.quoteSettings?.instructions) {
      quotesheetLite.customInstructions = formData?.quoteSettings?.instructions;
    }

    setTimeout(() => {
      setIsGenerating(false);
      (onGenerateLite || onGenerate)(quotesheetLite);
    }, 800);
  };

  // Auto-generate Quote Lite when autoGenerateLite flag is set
  React.useEffect(() => {
    if (autoGenerateLite) {
      handleGenerateLite();
      onAutoLiteComplete?.();
    }
  }, [autoGenerateLite]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Quote Lite — LEFT */}
        <div
          className="group relative border rounded-lg p-4 cursor-default transition-all duration-200 bg-surface border-border hover:bg-accent/5 hover:border-accent/40 hover:shadow-md"
        >
          {/* Hover tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 z-10 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
            <div className="bg-foreground text-background text-xs rounded-lg px-3 py-2 shadow-lg text-center leading-relaxed">
              <span className="font-semibold block mb-0.5">When to use Quote Lite</span>
              Quote Lite is for simpler projects, requesting the basics to get quotes done quickly — ideal when you just need price, timeline, and a brief scope.
            </div>
            <div className="w-2.5 h-2.5 bg-foreground rotate-45 mx-auto -mt-1.5" />
          </div>

          <div className="flex flex-col h-full">
            <div className="flex items-center mb-2">
              <Icon name="Zap" size={18} className="mr-2 text-accent group-hover:text-accent" />
              <h4 className="font-semibold text-foreground">Quote Lite</h4>
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-accent/10 text-accent font-medium">Simplified</span>
            </div>
            <p className="text-sm text-text-secondary mb-3 flex-1">
              A streamlined quotesheet requesting only the essentials — total price, timeline, and a brief scope summary — plus vendor contact and communication preferences.
            </p>
            <div className="flex flex-wrap items-center text-xs text-text-secondary gap-x-3 gap-y-1 mb-4">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1 text-success" />
                <span>Price &amp; timeline only</span>
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1 text-success" />
                <span>Contact preferences included</span>
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1 text-success" />
                <span>Quick for vendors to complete</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateLite}
              disabled={isGenerating}
              iconName={isGenerating ? "Loader" : "Zap"}
              iconPosition="left"
              className={`w-full ${isGenerating ? "animate-pulse" : ""}`}
            >
              {isGenerating ? 'Generating...' : 'Quote Lite'}
            </Button>
          </div>
        </div>

        {/* Custom Quote — RIGHT */}
        <div
          className="group relative border rounded-lg p-4 cursor-default transition-all duration-200 bg-primary/5 border-primary/20 hover:bg-primary/10 hover:border-primary/40 hover:shadow-md"
        >
          {/* Hover tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 z-10 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
            <div className="bg-foreground text-background text-xs rounded-lg px-3 py-2 shadow-lg text-center leading-relaxed">
              <span className="font-semibold block mb-0.5">When to use Custom Quote</span>
              Custom Quote is for complex projects that require detailed vendor responses — covering specs, credentials, pricing breakdowns, and more for thorough comparison.
            </div>
            <div className="w-2.5 h-2.5 bg-foreground rotate-45 mx-auto -mt-1.5" />
          </div>

          <div className="flex flex-col h-full">
            <div className="flex items-center mb-2">
              <Icon name="FileText" size={20} className="mr-2 text-primary" />
              <h4 className="font-semibold text-foreground">Custom Quotesheet</h4>
            </div>
            <p className="text-sm text-text-secondary mb-3 flex-1">
              Create a comprehensive question-based form tailored to your project needs.
              Vendors will answer questions about your specific requirements, and their responses will be captured for comparative analysis.
            </p>
            {!formData?.basics?.purchaseType && (
              <div className="mb-3 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={16} className="mr-2 text-warning mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">Purchase Type Required</p>
                    <p className="text-xs text-text-secondary mt-1">
                      Please go back to "Project Basics" (Step 1) and select a purchase type before generating the quotesheet.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center text-xs text-text-secondary gap-x-3 gap-y-1 mb-4">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1 text-success" />
                <span>Starts with project needs</span>
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1 text-success" />
                <span>Question-based format</span>
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={14} className="mr-1 text-success" />
                <span>Editable before use</span>
              </div>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleGenerate}
              disabled={isGenerating || !formData?.basics?.purchaseType}
              iconName={isGenerating ? "Loader" : "Sparkles"}
              iconPosition="left"
              className={`w-full ${isGenerating ? "animate-pulse" : ""}`}
            >
              {isGenerating ? 'Generating...' : 'Detailed Quote'}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default QuotesheetGenerator;